/**
 * DrawerRailTabStrip — the inline-mode Emails / Jobs / Card segmented
 * control rendered at the top of the CardDetailDrawer header when the
 * drawer is mounted inside the unified right rail.
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 * Behaviour is unchanged. The caller still gates rendering on
 * `inlineMode && railActiveTab && onRailTabChange`.
 */

export interface DrawerRailTabStripProps {
  railActiveTab: 'emails' | 'jobs' | 'card'
  onRailTabChange: (next: 'emails' | 'jobs' | 'card') => void
}

export function DrawerRailTabStrip({ railActiveTab, onRailTabChange }: DrawerRailTabStripProps) {
  return (
    <div className="border-b border-neutral-200 px-3 py-2 dark:border-zinc-800">
      <div
        role="tablist"
        aria-label="Right rail: Emails, Jobs, or Card"
        className="flex w-full items-center rounded-md bg-neutral-100 p-0.5 dark:bg-zinc-800"
      >
        {/* Tap-target floor: 44 px on phone (Apple HIG / WCAG
            2.5.5 §6 minimum). See parallel comment in
            KanbanEmailAllocationRail.tsx — same fix, same
            reason, same 2026-05-20 Newmark complaint. */}
        <button
          type="button"
          role="tab"
          aria-selected={railActiveTab === 'emails'}
          onClick={() => onRailTabChange('emails')}
          className={[
            'flex-1 basis-0 min-w-0 inline-flex items-center justify-center gap-1 rounded px-2 py-0.5 text-[10.5px] font-medium transition-colors',
            'min-h-[44px] md:min-h-0',
            railActiveTab === 'emails'
              ? 'bg-white text-neutral-900 shadow-sm dark:bg-zinc-900 dark:text-white'
              : 'text-neutral-500 hover:text-neutral-800 dark:text-zinc-400 dark:hover:text-zinc-200',
          ].join(' ')}
        >
          Emails
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={railActiveTab === 'jobs'}
          onClick={() => onRailTabChange('jobs')}
          className={[
            'flex-1 basis-0 min-w-0 inline-flex items-center justify-center gap-1 rounded px-2 py-0.5 text-[10.5px] font-medium transition-colors',
            'min-h-[44px] md:min-h-0',
            railActiveTab === 'jobs'
              ? 'bg-white text-neutral-900 shadow-sm dark:bg-zinc-900 dark:text-white'
              : 'text-neutral-500 hover:text-neutral-800 dark:text-zinc-400 dark:hover:text-zinc-200',
          ].join(' ')}
        >
          Jobs
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={railActiveTab === 'card'}
          onClick={() => onRailTabChange('card')}
          className={[
            'flex-1 basis-0 min-w-0 inline-flex items-center justify-center gap-1 rounded px-2 py-0.5 text-[10.5px] font-medium transition-colors',
            'min-h-[44px] md:min-h-0',
            railActiveTab === 'card'
              ? 'bg-white text-neutral-900 shadow-sm dark:bg-zinc-900 dark:text-white'
              : 'text-neutral-500 hover:text-neutral-800 dark:text-zinc-400 dark:hover:text-zinc-200',
          ].join(' ')}
        >
          Card
        </button>
      </div>
    </div>
  )
}
